import React, { useEffect, useMemo, useState } from 'react';

import { toast } from 'react-toastify';

import { useRoleSwitcher } from '../../../../hooks/useRoleSwitcher';
import AppModal from '../../../../ui-components/AppModal';
import Dropdown from '../../../../ui-components/Dropdown';
import type { AppModalActionProps } from '../../../../model/appModalActionProps';

import {
  useEditPushNotificationMutation,
  useLazyGetPushNotificationDetailQuery,
} from '../pushNotificationsApi';

import type { PushNotificationDetail } from '../model';

const SUPER_ADMIN_ROLE_CODES = ['HBADMIN', 'SUPER_ADMIN', 'SUPERADMIN'];

interface PushNotificationEditModalProps {
  isOpen: boolean;
  notificationTemplateId?: string;
  onClose: () => void;
  onSuccess?: () => void;
}

const STATUS_OPTIONS = [
  { label: 'Active', value: 'active' },
  { label: 'Inactive', value: 'inactive' },
];

const PushNotificationEditModal: React.FC<PushNotificationEditModalProps> = ({
  isOpen,
  notificationTemplateId,
  onClose,
  onSuccess,
}) => {
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [status, setStatus] = useState('');
  const { activeRoleCode } = useRoleSwitcher();
  const canManageStatus = SUPER_ADMIN_ROLE_CODES.includes(
    (activeRoleCode || '').trim().toUpperCase()
  );

  const [fetchDetail, { data, isFetching, error }] =
    useLazyGetPushNotificationDetailQuery();
  const [updateNotification, { isLoading }] = useEditPushNotificationMutation();

  useEffect(() => {
    if (isOpen && notificationTemplateId) {
      fetchDetail(notificationTemplateId);
    }
  }, [fetchDetail, notificationTemplateId, isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setTitle('');
      setMessage('');
      setStatus('');
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const detail = data?.data;
    if (detail) {
      setTitle(detail.title ?? '');
      setMessage(detail.message ?? '');
      setStatus(detail.status ?? 'active');
    }
  }, [data, isOpen]);

  const templateInfo = useMemo<PushNotificationDetail | undefined>(
    () => data?.data,
    [data]
  );

  const handleSave = async () => {
    if (!notificationTemplateId) return;
    try {
      await updateNotification({
        id: notificationTemplateId,
        title,
        message,
        ...(canManageStatus && status ? { status } : {}),
      }).unwrap();
      onSuccess?.();
      onClose();
    } catch (saveError: any) {
      toast.error(
        saveError?.data?.message ||
          saveError?.message ||
          'Failed to update push notification'
      );
    }
  };

  const actions: AppModalActionProps[] = [
    {
      label: 'Cancel',
      variant: 'secondary',
      onClick: onClose,
      disabled: isLoading,
    },
    {
      label: 'Save changes',
      variant: 'primary',
      onClick: handleSave,
      loading: isLoading,
      disabled: isLoading,
    },
  ];

  return (
    <AppModal
      isOpen={isOpen}
      title="Edit Push Notification"
      onClose={onClose}
      actions={actions}
      hideFooter={false}
      variant="form"
      closeOnOverlayClick
      maxWidth="max-w-3xl"
    >
      {isFetching ? (
        <div className="flex items-center justify-center py-12">
          <p className="text-sm text-neutral-500">Loading template...</p>
        </div>
      ) : error ? (
        <div className="flex items-center justify-center py-12">
          <p className="text-sm text-error-500">
            Unable to load push notification template. Please try again.
          </p>
        </div>
      ) : (
        <div className="space-y-5">
          <div>
            <label className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
              Template Name
            </label>
            <input
              type="text"
              value={templateInfo?.templateName ?? ''}
              readOnly
              className="input mt-1.5 bg-neutral-50 dark:bg-neutral-900 cursor-not-allowed border-neutral-200 dark:border-neutral-800"
            />
            <p className="mt-1.5 text-xs text-neutral-500">
              Template names are predefined and cannot be changed
            </p>
          </div>

          {canManageStatus ? (
            <div>
              <Dropdown
                label="Status"
                value={status}
                setValue={(value) => setStatus(String(value))}
                options={STATUS_OPTIONS}
                labelKey="label"
                valueKey="value"
                isCreatable={false}
                placeholder="Select status"
              />
              <p className="mt-1.5 text-xs text-neutral-500">
                Only SuperAdmin can activate or inactivate this template.
              </p>
            </div>
          ) : null}

          <div>
            <label className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
              Push Title <span className="text-error-500">*</span>
            </label>
            <input
              type="text"
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              className="input mt-1.5"
              placeholder="Enter push title"
              maxLength={100}
            />
            <p className="mt-1.5 text-xs text-neutral-500">
              {title.length}/100 characters
            </p>
          </div>

          <div>
            <label className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
              Push Message <span className="text-error-500">*</span>
            </label>
            <textarea
              value={message}
              onChange={(event) => setMessage(event.target.value)}
              className="input mt-1.5 min-h-32 resize-y"
              placeholder="Enter push message"
              maxLength={250}
            />
            <p className="mt-1.5 text-xs text-neutral-500">
              {message.length}/250 characters
            </p>
          </div>
        </div>
      )}
    </AppModal>
  );
};

export default PushNotificationEditModal;
